import React, { useEffect, useMemo, useState } from 'react';
import { getPlatformUserId, isEmpty } from '../../../../../utils/utils';
import styles from './AddUserModal.module.scss';
import {
    COUNTRY_CODE,
    EMAIL,
    EMAIL_ERROR_MSG,
    FULL_NAME,
    FULL_NAME_ERROR_MSG,
    JOB_ROLE,
    ORG_NAME,
    PASSWORD,
    PHONE,
    PHONE_ERROR_MSG,
    TRIGGER_NOTIFICATION,
    USER_DESIGNATION,
} from '../../../../../utils/constants/companyLogin';
import { InternalSummonAicaDataPayload, InternalSummonUserType } from '../../../../../store/user/actions';
import { useAppSelector } from '../../../../../app/hooks';
import { createAccountV2 } from '../../../../../store/user/userV2/actions';
import { USER_TYPE, JOURNEY_NAME } from '../../../../../utils/constants/user';
import { useDispatch } from 'react-redux';
import { Button, Input, Modal } from '../../../../../common/_custom';
import CountryCodesDropdown, { CountryCodeData } from '../../../../Company/CompanyLogin/CountryCodesDropdown/CountryCodesDropdown';
import { InviteUserFormValidations, partnerFormValidations } from '../../../../../utils/constants/formValidations';
import { MANAGE_USERS_TABS } from '../../../../../store/investor/constants';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchOwners, GetOwnerDataPayload } from '../../../../../store/investor/action';
import { toast } from 'react-toastify';
import CheckBox from '../../../../../common/CheckBox/CheckBox';
import { addAssessee } from '../../../../../store/user/userV2/actions';
import Dropdown from '../../../../../common/_custom/Dropdown';
import { DropdownOption, CustomOptionConfig } from '../../../../../common/_custom/Dropdown/dropdownTypes';
import { setShowManageUserDrawer } from '../../../../../store/company/action';
import { useVisibilityConfig } from '../../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../../utils/constants/visibilityKeys';
import { USER_TYPE_OPTIONS, USER_ROLE_KEY, DEFAULT_TEAM_ROLE, SUPER_ADMIN_ROLE, buildRoleOptions } from '../../ManageUserConstants';
import { useAssignRoleAfterCreate } from '../../useAssignRoleAfterCreate';
const { PARTNER, INVESTOR, TEAM } = MANAGE_USERS_TABS;

interface AddUserModalProps {
    showAddUserModal: boolean;
    setShowAddUserModal: (value: boolean | ((prevState: boolean) => boolean)) => void;
    currentRowUser: any;
    onClose: () => void;
    fetchRows: (pgNo: number) => Promise<void>;
    getDynamicHeaders?: () => void;
    pageNo: number;
    onUserAdded?: (user: any) => void;
    isOrgDrawer?: boolean;
    allowUserTypeEdit?: boolean;
}
const USER_TYPE_KEY = 'userType';
export type AddUserData = {
    [FULL_NAME]: string;
    [EMAIL]: string;
    [PHONE]: string;
    [COUNTRY_CODE]: CountryCodeData | null;
    [USER_TYPE_KEY]?: string;
    [TRIGGER_NOTIFICATION]: boolean;
    [USER_DESIGNATION]?: DropdownOption | null;
    [JOB_ROLE]: DropdownOption | null;
    [USER_ROLE_KEY]?: DropdownOption | null;
};

const addLenderOptions = [
    {
        id: 'LENDER',
        label: 'Admin',
    },
    {
        id: 'LENDER_TEAM',
        label: 'Team Member',
    },
];

export const DEFAULT_JOB_ROLES = [
    { id: 'accountant', label: 'Accountant' },
    { id: 'ceo', label: 'Chief Executive Officer (CEO)' },
    { id: 'cfo', label: 'Chief Financial Officer (CFO)' },
    { id: 'collections_manager', label: 'Collections Manager' },
    { id: 'compliance_officer', label: 'Compliance Officer' },
    { id: 'credit_analyst', label: 'Credit Analyst' },
    { id: 'customer_support_manager', label: 'Customer Support Manager' },
    { id: 'finance_manager', label: 'Finance Manager' },
    { id: 'it_support_specialist', label: 'IT Support Specialist' },
    { id: 'operations_manager', label: 'Operations Manager' },
    { id: 'owner_founder', label: 'Owner / Founder' },
    { id: 'referral_partner', label: 'Referral Partner' },
    { id: 'relationship_manager', label: 'Relationship Manager' },
    { id: 'underwriter', label: 'Underwriter' },
];

function AddUserModal(props: AddUserModalProps) {
    const {
        showAddUserModal,
        setShowAddUserModal,
        currentRowUser,
        onClose,
        fetchRows,
        pageNo,
        getDynamicHeaders,
        onUserAdded,
        isOrgDrawer,
        allowUserTypeEdit = false,
    } = props;
    const user = useAppSelector((state) => state.user);
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const userType = user.userData.accountType;
    const domainMetadata = user.domainMetadata;
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const { canWrite, roleCatalog } = useVisibilityConfig();
    const canEditRole = canWrite(VISIBILITY_KEYS.USER_MANAGEMENT.EDIT_USER_ROLE);
    const { assignRoleAfterCreate } = useAssignRoleAfterCreate();
    const showInviteDropdown = [INVESTOR].includes(searchParams.get('category')?.toString() ?? '') && !allowUserTypeEdit;
    const showTeamRoleDropdown = canEditRole && (searchParams.get('category')?.toString() === TEAM || allowUserTypeEdit);
    const getInitialUserDesignation = (searchParams: URLSearchParams) =>
        searchParams.get('category')?.toString() === 'Investors' ? addLenderOptions[0] : null;
    const catalogResult = useMemo(() => (Object.keys(roleCatalog ?? {}).length > 0 ? buildRoleOptions(roleCatalog) : null), [roleCatalog]);
    const teamRoleOptionsList = catalogResult ? catalogResult.options : USER_TYPE_OPTIONS;
    const teamRoleDefaultId = catalogResult ? catalogResult.defaultId : DEFAULT_TEAM_ROLE;
    const getInitialTeamRole = (): DropdownOption => teamRoleOptionsList.find((o) => o.id === teamRoleDefaultId) ?? teamRoleOptionsList[0];
    const currentCategory = searchParams.get('category')?.toString();
    const isTeamUserFlow = currentCategory === TEAM || allowUserTypeEdit;
    const assesseeOrgId = searchParams.get('orgId') || params.id || '';

    const [formData, setFormData] = useState<AddUserData>({
        [FULL_NAME]: '',
        [EMAIL]: '',
        [PHONE]: '',
        [COUNTRY_CODE]: null,
        [USER_TYPE_KEY]: '',
        [TRIGGER_NOTIFICATION]: true,
        [USER_DESIGNATION]: getInitialUserDesignation(searchParams),
        [JOB_ROLE]: null,
        [USER_ROLE_KEY]: getInitialTeamRole(),
    });
    const getUserType = (userType) => {
        switch (userType) {
            case PARTNER:
                return {
                    id: 'PARTNER',
                    label: 'Partner',
                };
            default:
                return { id: '', label: '' };
        }
    };
    const [errors, setErrors] = useState({
        [FULL_NAME]: false,
        [EMAIL]: false,
        [PHONE]: false,
        [JOB_ROLE]: false,
    });

    useEffect(() => {
        setFormData((prevData) => ({
            [FULL_NAME]: '',
            [EMAIL]: '',
            [PHONE]: '',
            [COUNTRY_CODE]: prevData[COUNTRY_CODE],
            [USER_TYPE_KEY]: '',
            [TRIGGER_NOTIFICATION]: true,
            [USER_DESIGNATION]: getInitialUserDesignation(searchParams),
            [JOB_ROLE]: null,
            [USER_ROLE_KEY]: getInitialTeamRole(),
        }));
    }, [searchParams]);

    const disabled = !(formData[FULL_NAME] && formData[EMAIL] && formData[COUNTRY_CODE]);
    const handleFormChange = (val: string | boolean | DropdownOption, identifier: string) => {
        setErrors((errors) => ({
            ...errors,
            [identifier]: false,
        }));
        setFormData((data) => ({
            ...data,
            [identifier]: val,
        }));
    };

    const getSummonUserType = (userType: string) => {
        switch (userType) {
            case 'Partner':
                return InternalSummonUserType.PARTNER;
            case 'Lender':
                return InternalSummonUserType.LENDER;
            case 'Team member':
                return InternalSummonUserType.ADMIN_TEAM;
            default:
                return null;
        }
    };
    const fetchOwnerDetails = () => {
        let payload: GetOwnerDataPayload = {
            investorOrgId: user?.userData?.investorOrgId,
        };
        dispatch(fetchOwners(payload));
    };

    const handleSuccess = async (orgId: string, res: any) => {
        const isLenderModal = searchParams.get('category')?.toString() === 'Investors';
        const isTeamModal = searchParams.get('category')?.toString() === TEAM || allowUserTypeEdit;
        const newUser = {
            name: formData[FULL_NAME],
            emailId: formData[EMAIL]?.toLowerCase(),
            mobileNumber: !isEmpty(formData[PHONE]) ? `+${formData[COUNTRY_CODE]?.callingCode} ${formData[PHONE]}` : '',
            countryCode: !isEmpty(formData[PHONE]) ? formData[COUNTRY_CODE]?.countryCode : '',
            organizationId: orgId,
            userStatus: 'Unverified',
            jobRole: formData[JOB_ROLE]?.label || '',
        };

        if (isLenderModal) {
            newUser['userType'] = formData[USER_DESIGNATION]?.id || '';
        }
        if (res?.isExistingUser !== true) {
            toast.success('User invited successfully');
            onUserAdded?.(newUser);
            dispatch(setShowManageUserDrawer(false));
        } else {
            toast.success('User already exists');
        }
        setLoading(false);
        onClose();
        fetchRows(pageNo);

        if (isTeamModal && showTeamRoleDropdown && formData[USER_ROLE_KEY]?.id) {
            await assignRoleAfterCreate(formData[EMAIL], formData[USER_ROLE_KEY].id.toString(), user.domainMetadata?.investorOrgId || '');
        }

        if (userType !== USER_TYPE.COMPANY) {
            fetchOwnerDetails();
            getDynamicHeaders?.();
            setFormData({
                [FULL_NAME]: '',
                [EMAIL]: '',
                [PHONE]: '',
                [COUNTRY_CODE]: formData[COUNTRY_CODE],
                [USER_TYPE_KEY]: getUserType(searchParams.get('category')?.toString())?.id,
                [TRIGGER_NOTIFICATION]: true,
                [USER_DESIGNATION]: getInitialUserDesignation(searchParams),
                [JOB_ROLE]: null,
                [USER_ROLE_KEY]: getInitialTeamRole(),
            });
        }
    };

    const handleSubmit = () => {
        setErrors({
            [FULL_NAME]: !InviteUserFormValidations.isFullNameValid(formData[FULL_NAME]),
            [EMAIL]: !InviteUserFormValidations.isEmailValid(formData[EMAIL]),
            [PHONE]: !InviteUserFormValidations.isPhoneValid(formData[PHONE], formData[COUNTRY_CODE]),
            [JOB_ROLE]: false,
        });
        if (!InviteUserFormValidations.allValid(formData)) return;
        setLoading(true);
        if (userType === USER_TYPE.COMPANY || (isOrgDrawer && currentCategory === MANAGE_USERS_TABS.COMPANY)) {
            dispatch(
                addAssessee({
                    payload: {
                        user: {
                            emailId: formData[EMAIL],
                            mobileNumber: formData[PHONE],
                            name: formData[FULL_NAME],
                            countryCode: formData[COUNTRY_CODE]?.countryCode ?? '',
                            accountType: USER_TYPE.COMPANY,
                            accountTypes: [USER_TYPE.COMPANY],
                            journeyName: JOURNEY_NAME.ADD_BORROWER_USER,
                            countryId: formData[COUNTRY_CODE]?.countryId ?? '',
                            investorId: user.userData.investorOrgId,
                            organizationId: user.userData.assesseeOrgId || assesseeOrgId,
                            triggerNotification: formData[TRIGGER_NOTIFICATION],
                            customFields: {
                                jobRole: formData[JOB_ROLE]?.label || '',
                            },
                        },
                        platformId: domainMetadata?.productName ?? '',
                    },
                    handlers: {
                        onSuccess: (res) => handleSuccess(user.userData.assesseeOrgId || assesseeOrgId, res),
                        onError: () => {
                            setLoading(false);
                        },
                    },
                }),
            );
        } else {
            const orgId =
                currentRowUser?.organizationId ??
                currentRowUser?.[0]?.organizationId ??
                sessionStorage.getItem('user_org_id') ??
                localStorage.getItem('user_org_id') ??
                '';
            dispatch(
                createAccountV2(
                    {
                        user: {
                            name: formData[FULL_NAME],
                            emailId: formData[EMAIL]?.toLowerCase(),
                            investorId: user.domainMetadata?.investorOrgId || '',
                            accountType: isTeamUserFlow
                                ? USER_TYPE.ADMIN_TEAM
                                : formData[USER_DESIGNATION]?.id
                                ? formData[USER_DESIGNATION]?.id.toString()
                                : getUserType(searchParams.get('category')?.toString())?.id,
                            accountTypes: [
                                USER_TYPE.INVESTOR,
                                USER_TYPE.PARTNER,
                                USER_TYPE.ADMIN_TEAM,
                                USER_TYPE.LENDER,
                                USER_TYPE.LENDER_TEAM,
                            ],
                            journeyName:
                                currentCategory === 'Investors'
                                    ? JOURNEY_NAME.INVITE_LENDER
                                    : isTeamUserFlow
                                    ? JOURNEY_NAME.INVITE_TEAM_MEMBER
                                    : JOURNEY_NAME.INVITE_PARTNER,
                            mobileNumber: formData[PHONE],
                            countryId: formData[COUNTRY_CODE]?.countryId || '',
                            triggerNotification: formData[TRIGGER_NOTIFICATION],
                            countryCode: formData[COUNTRY_CODE]?.countryCode || '',
                            orgId: orgId,
                            customFields: {
                                jobRole: formData[JOB_ROLE]?.label || '',
                            },
                        },
                        platformId: user.domainMetadata?.productName || '',
                    },
                    {
                        isInviteCall: true,
                        onSuccess: (res) => handleSuccess(orgId, res),
                        onError: () => {
                            setLoading(false);
                        },
                    },
                ),
            );
        }
    };
    return (
        <Modal
            open={showAddUserModal}
            onClose={() => {
                setShowAddUserModal(false);
                setFormData({
                    [FULL_NAME]: '',
                    [EMAIL]: '',
                    [PHONE]: '',
                    [COUNTRY_CODE]: formData[COUNTRY_CODE],
                    [USER_TYPE_KEY]: '',
                    [TRIGGER_NOTIFICATION]: true,
                    [USER_DESIGNATION]: getInitialUserDesignation(searchParams),
                    [JOB_ROLE]: null,
                    [USER_ROLE_KEY]: getInitialTeamRole(),
                });
            }}
            persistent
            size={'flexible'}
            style={{ padding: '10px' }}
        >
            <div className={styles.FormBox}>
                <div className={styles.Title}>Add User</div>
                <div className={styles.FormContainer}>
                    <Input
                        placeholder="Full Name"
                        value={formData[FULL_NAME]}
                        onChange={(val: string) => handleFormChange(val, FULL_NAME)}
                        errorMessage={FULL_NAME_ERROR_MSG}
                        isValid={!errors[FULL_NAME]}
                        allowAllCharacters={true}
                    />
                    <Input
                        placeholder="Email"
                        value={formData[EMAIL]}
                        onChange={(val: string) => handleFormChange(val.trim(), EMAIL)}
                        errorMessage={EMAIL_ERROR_MSG}
                        isValid={!errors[EMAIL]}
                        allowAllCharacters={true}
                    />
                    <div className={styles.Row}>
                        <CountryCodesDropdown
                            //@ts-ignore
                            setCountryCode={(val) => handleFormChange(val, COUNTRY_CODE)}
                        />
                        <Input
                            type="number"
                            placeholder="Phone Number"
                            value={formData[PHONE]}
                            onChange={(value) => {
                                if (/^\d*$/.test(value) && value.length <= 10) {
                                    handleFormChange(value, PHONE);
                                }
                            }}
                            errorMessage={PHONE_ERROR_MSG}
                            isValid={!errors[PHONE]}
                            style={{ marginTop: '0', width: '100%' }}
                        />
                    </div>
                    <Dropdown
                        optionsList={DEFAULT_JOB_ROLES}
                        selectedOption={formData[JOB_ROLE]}
                        itemClickHandler={(option) => {
                            handleFormChange(option, JOB_ROLE);
                        }}
                        placeholder="Job Role"
                        label="Job Role"
                        enableSearch={true}
                        hideOtherOptionWhenNoMatch={true}
                        customOption={{
                            enabled: true,
                            text: 'Add Other Job Role',
                            placeholder: 'Enter job role...',
                            maxLength: 50,
                        }}
                        style={{ marginTop: '30px' }}
                    />

                    {showTeamRoleDropdown && (
                        <Dropdown
                            optionsList={teamRoleOptionsList}
                            selectedOption={formData[USER_ROLE_KEY] ?? null}
                            itemClickHandler={(val) => handleFormChange(val, USER_ROLE_KEY)}
                            placeholder="User Type"
                            style={{ marginTop: '1.875rem' }}
                            label="User Type"
                            hideClearIcon
                        />
                    )}
                    {showInviteDropdown && (
                        <Dropdown
                            optionsList={addLenderOptions}
                            selectedOption={
                                formData[USER_DESIGNATION]
                                    ? formData[USER_DESIGNATION]
                                    : getUserType(searchParams.get('category')?.toString())
                            }
                            itemClickHandler={(val) => {
                                handleFormChange(val, USER_DESIGNATION);
                            }}
                            placeholder="Select User Type"
                            style={{ marginTop: '30px' }}
                            label="Select User Type"
                            hideClearIcon
                        />
                    )}
                    <CheckBox
                        label={'Send invite on email'}
                        checked={formData[TRIGGER_NOTIFICATION]}
                        onCheck={(val) => handleFormChange(val, TRIGGER_NOTIFICATION)}
                        variant={'tertiary'}
                        style={{ padding: '1.5rem 0 0 0.25rem' }}
                    />
                    <Button
                        variant={'primary'}
                        style={{ width: '100%' }}
                        text="Add"
                        loadingText="Please Wait"
                        loading={loading}
                        onClick={handleSubmit}
                        disabled={disabled}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default AddUserModal;
